/**
 * Event identity, and the scope an impression is counted in.
 *
 * A leaf module: nothing here but the store and Zod. Every component that
 * counts an impression reaches for `claimOncePerSession` through
 * `useTrackOnMount`, and living in `base.analytics.ts` would drag GTM,
 * Mixpanel, Sentry, firebase and i18n into all of them.
 */
import { z } from "zod";
import { sessionStore } from "../../lib/store";

/**
 * A de-duplication id for one emitted event.
 *
 * `crypto.randomUUID` is defined only in a SECURE context, so a dev build served
 * over http to a phone does not have it. Unguarded, the throw happens inside
 * telemetry and takes the caller with it — including a click handler that was
 * about to navigate.
 */
export function newEventId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * The seed every id of this session derives from, and the facts it has already
 * counted. Both in `sessionStorage`, which is what makes the scope the tab's
 * VISIT rather than one page load.
 *
 * The difference is not academic here: this app reloads itself mid-visit, and
 * on the reader's return. The service worker runs under `autoUpdate` and calls
 * `registration.update()` on every `visibilitychange`, and the white-screen net
 * reloads on resume (both in `index.tsx`). Held in memory, the seed would be
 * re-minted at each of those, so the same card would get a second, different
 * `sourceEventId` and the `@unique` column could not tell it was one impression
 * — the same undercount this whole change exists to remove, just rarer.
 *
 * They die with the tab, which is the honest end of a visit: a webview the OS
 * destroyed and relaunched is a reader arriving again.
 */
const seedStore = sessionStore<string | null>(
  "analytics-session-seed",
  null,
  z.string().nullable()
);
const claimedFactsStore = sessionStore<string[]>(
  "analytics-session-claims",
  [],
  z.array(z.string())
);

/** Minted on first use, so a session that counts nothing stores nothing. */
function sessionSeed(): string {
  const stored = seedStore.get();
  if (stored !== null) return stored;
  const seed = newEventId();
  seedStore.set(seed);
  return seed;
}

/**
 * True the first time this session is asked about `fact`, false after.
 *
 * The counting unit of an impression is the (session, thing shown), never the
 * mount. Deduping per mount is what capped the practice rail's interaction rate
 * near 50% by construction: opening a card navigates away, coming back remounts
 * the counter, and the measured interaction minted its own denominator event.
 * The same remount impressed one card four times.
 */
export function claimOncePerSession(fact: string): boolean {
  const claimed = claimedFactsStore.get();
  if (claimed.includes(fact)) return false;
  claimedFactsStore.set([...claimed, fact]);
  return true;
}

/**
 * A `sourceEventId` that fingerprints the fact instead of the emission.
 *
 * `user_content_events.source_event_id` is `@unique` and the ETL's idempotency
 * key, and `createMany({ skipDuplicates: true })` dedups on it — which a fresh
 * uuid per call turns into decoration. Seeded per session because the column is
 * globally unique: the fact alone collides between two readers who were shown
 * the same card.
 *
 * A real uuid wherever the app is served over https, which is every deployed
 * environment. On the http dev build the fallback above seeds it instead, so
 * the guarantee there is a millisecond plus 46 bits, and a seed collision drops
 * a whole session's impressions rather than one row.
 */
export function sessionScopedEventId(fact: string): string {
  return `${sessionSeed()}:${fact}`;
}

/**
 * End the counting session, because the authenticated identity is changing.
 *
 * Called from `useLogOutUser`, beside the other per-account clears. Logging out
 * is an SPA navigation with no reload, so without this the next account on the
 * device is shown a card this one already claimed and its impression is
 * dropped. The seed goes with it: kept, the id derived for that card would
 * collide with the row the previous account already wrote against the `@unique`
 * column, and the insert would skip it in silence.
 */
export function resetSessionEventScope(): void {
  claimedFactsStore.set([]);
  seedStore.set(null);
}
